import { Injectable, inject } from '@angular/core'
import type { HttpErrorResponse } from '@angular/common/http'
import { ActivatedRoute, Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, exhaustMap, map } from 'rxjs/operators'
import {
  login,
  loginFailure,
  loginSuccess,
  logout,
  logoutSuccess,
} from './authentication.actions'
import { AuthApiService } from '@/app/core/services/api/authentication/auth-api.service'
import { AuthenticationService } from '@/app/core/services/auth.service'
import { ResponseStatusCode } from '@/app/domain/enums/response-status-code.enum'
import { getDefaultLandingPath } from '@/app/common/nav-entries'

/** Real HTTP-level failure — network error, or a FluentValidation 400 ProblemDetails. */
function transportErrorMessage(error: HttpErrorResponse): string {
  return error.error?.detail ?? error.error?.title ?? 'Unable to reach the server.'
}

@Injectable()
export class AuthenticationEffects {
  private actions$ = inject(Actions)
  private authApi = inject(AuthApiService)
  private authService = inject(AuthenticationService)
  private router = inject(Router)
  private route = inject(ActivatedRoute)

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      exhaustMap(({ email, password }) =>
        this.authApi.login({ email, password }).pipe(
          map((response) => {
            // Almost every endpoint returns HTTP 200 regardless of outcome — bad
            // credentials come back as statusResult.code = Unauthorized, not a 401.
            if (response.statusResult.code !== ResponseStatusCode.Success) {
              return loginFailure({ error: response.statusResult.description })
            }

            const { authResult, employee, menus } = response.result
            this.authService.saveSession(authResult, employee, menus)

            // No explicit returnUrl (the common case — a plain sign-in, not a bounce-back from a
            // deep link) lands the user on their configured dashboard if granted, otherwise their
            // highest-priority granted page, per the Menu table's own DisplayOrder — rather than
            // the app's hardcoded '/' -> 'dashboard' default, which ignores what's actually been
            // configured for this user.
            const returnUrl = this.route.snapshot.queryParams['returnUrl'] || getDefaultLandingPath(menus)
            this.router.navigateByUrl(returnUrl)

            return loginSuccess({ employee, authResult, menus })
          }),
          catchError((error: HttpErrorResponse) =>
            of(loginFailure({ error: transportErrorMessage(error) }))
          )
        )
      )
    )
  )

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      exhaustMap(() => {
        const refreshToken = this.authService.refreshToken
        this.authService.clearSession()
        this.router.navigate(['/auth/sign-in'])

        if (!refreshToken) {
          return of(logoutSuccess())
        }

        // Best-effort — the local session is already cleared either way.
        return this.authApi.revoke({ refreshToken }).pipe(
          map(() => logoutSuccess()),
          catchError(() => of(logoutSuccess()))
        )
      })
    )
  )
}
